import React, { FC, useEffect, useMemo, useRef } from 'react';

import { V1CloudEventsResourceRow } from '@ariksa/audittrail/api';
import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Stack, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { PermissionsActionsIcon, UserIcon } from 'components/Icons';
import { WithResourceIcon } from 'components/index';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';

import { SummaryCountTag } from './SummaryCountTag';

interface Props {
  currentResource?: V1CloudEventsResourceRow;
  insight?: Record<string, any>;
  resourceType: NativeResources;
  isOpen: boolean;
}

const getCountByType = (
  counts: Record<string, { total?: number }> | undefined,
  matcher: RegExp,
) => {
  const entry = Object.entries(counts ?? {}).find(([type]) =>
    matcher.test(type.toLowerCase()),
  );

  return entry?.[1]?.total ?? 0;
};

const SummaryIcon: FC<{
  icon: React.ReactNode;
  color: string;
  children: React.ReactNode;
}> = ({ icon, color, children }) => (
  <WithResourceIcon
    icon={icon}
    color={color}
    spacing={3}
    hStackStyles={{ align: 'flex-start', w: 'full' }}
    iconStyles={{ mt: 0.5 }}
    iconSize="xs"
  >
    {children}
  </WithResourceIcon>
);

export const AccessSummary: FC<Props> = ({
  currentResource,
  insight,
  resourceType,
  isOpen,
}) => {
  const dispatch = useDispatch();
  const { accessSummaryCounts } = useSelector(selectIncidentResponse);
  const requestedKey = useRef<string>();

  const uuid = insight?.uuid;
  const accountId = insight?.account ?? currentResource?.account_id;
  const requestKey = JSON.stringify([uuid, accountId, resourceType]);

  useEffect(() => {
    if (!isOpen || !uuid || !accountId || !resourceType) return;
    if (requestedKey.current === requestKey) return;

    requestedKey.current = requestKey;
    dispatch(
      actions.getAccessSummary({
        q: {
          uuid: String(uuid),
          accountId: String(accountId),
          nativeResourceType: resourceType,
        },
      }),
    );
  }, [accountId, dispatch, isOpen, requestKey, resourceType, uuid]);

  const summary = accessSummaryCounts.data?.[0];
  const userCount = getCountByType(summary?.principal_counts_by_type, /user/);
  const roleCount = getCountByType(summary?.principal_counts_by_type, /role/);
  const trustedEntities = useMemo(
    () =>
      Object.entries(summary?.trusted_entity_counts_by_type ?? {}).filter(
        ([, value]) => (value?.total ?? 0) > 0,
      ),
    [summary?.trusted_entity_counts_by_type],
  );
  const principalTypes = Object.entries(
    summary?.principal_counts_by_type ?? {},
  ).filter(([, value]) => (value?.total ?? 0) > 0);
  if (accessSummaryCounts.isLoading) {
    return <CustomSpinner loading size="lg" />;
  }

  return (
    <BorderedBox
      h="full"
      minH={0}
      minW={0}
      px={0}
      w="25%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      pb={4}
      pt={3}
    >
      <Box
        px={5}
        pb={3}
        borderBottom="1px solid"
        borderColor="componentBorder"
        fontSize="lg"
        color={customTheme.colors.gray['300']}
      >
        <Text>Access Summary</Text>
      </Box>
      <Stack
        px={5}
        py={6}
        spacing={8}
        flex={1}
        minH={0}
        overflowY="auto"
        overflowX="hidden"
      >
        <SummaryIcon color={customTheme.colors.blue['300']} icon={<UserIcon />}>
          <Box>
            <Text as="span">
              {summary?.total_principal_count ?? 0} principals that include{' '}
              {userCount} users and {roleCount} IAM roles.
              {trustedEntities.length ? ' IAM roles have ' : ''}
            </Text>
            {trustedEntities.map(([type, value]) => (
              <SummaryCountTag
                key={type}
                type={type}
                total={value.total}
                color={customTheme.colors.blue['300']}
              />
            ))}
            {trustedEntities.length ? ' as trusted entities' : ''}
          </Box>
        </SummaryIcon>
        <SummaryIcon
          color={customTheme.colors.primary}
          icon={<PermissionsActionsIcon />}
        >
          <Text>
            {summary?.high_privilege_principal_count ?? 0} principals have
            highly privileged permissions like UPDATE and DELETE
          </Text>
        </SummaryIcon>
        <SummaryIcon
          color={customTheme.colors.primary}
          icon={<PermissionsActionsIcon />}
        >
          <Text as="span">
            Access includes {summary?.direct_access_count ?? 0} direct
            principals and {summary?.via_role_access_count ?? 0} principals
            through roles. Principal types:{' '}
          </Text>
          {principalTypes.length ? (
            principalTypes.map(([type, value]) => (
              <SummaryCountTag
                key={type}
                type={type}
                total={value.total}
                color={customTheme.colors.primary}
              />
            ))
          ) : (
            <Text as="span"> None.</Text>
          )}
          {principalTypes.length > 0 && <Text as="span">.</Text>}
        </SummaryIcon>
      </Stack>
    </BorderedBox>
  );
};
